import { useEffect, useState } from "react";
import { debounce } from "lodash";

const mobileViewWidthThresholdInPixels = 800;
const mobileViewHeightThresholdInPixels = 700;

const screenWidth = () => {
  if (import.meta.hot) {
    // @ts-expect-error dev-only
    return window.dev?.screenWidth ?? window.screen.width;
  }
  return window.screen.width;
};

const screenHeight = () => {
  if (import.meta.hot) {
    // @ts-expect-error dev-only
    return window.dev?.screenHeight ?? window.screen.height;
  }
  return window.screen.height;
};

export const isDesktopScreen = (): boolean => {
  const isPortrait = window.matchMedia("(orientation: portrait)").matches;
  return (
    (screenWidth() >= mobileViewWidthThresholdInPixels && isPortrait) ||
    (screenHeight() >= mobileViewHeightThresholdInPixels && !isPortrait)
  );
};

export const isMobileView = (): boolean => !isDesktopScreen();

export type Breakpoint = "sm" | "md" | "lg" | "xl" | "xxl";

// Ref: components-v2/index.css
const breakpoints: Record<Breakpoint, number> = {
  ["sm"]: 40 * 16,
  ["md"]: 48 * 16,
  ["lg"]: 64 * 16,
  ["xl"]: 80 * 16,
  ["xxl"]: 96 * 16,
};

export const useWindowWidth = (): Breakpoint => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const listener = debounce(() => setWidth(window.innerWidth), 125);
    window.addEventListener("resize", listener);
    return () => {
      window.removeEventListener("resize", listener);
    };
  });
  return (
    (Object.entries(breakpoints)
      .toReversed()
      .find(([, w]) => width >= w)?.[0] as Breakpoint) ?? "sm"
  );
};

export const useIsBelowBreakpoint = (b: Breakpoint): boolean => {
  const current = useWindowWidth();
  return breakpoints[current] < breakpoints[b];
};

export const pdfWidthMapping: Record<"sm" | "md" | "lg", number> = {
  sm: 600,
  md: 900,
  lg: 1200,
};
